// Client-side voice pool label resolver.
//
// Mirrors the server VOICE_POOL in functions/lib/voices.ts (50 voices) for
// UI labelling purposes only — the actual ElevenLabs voice_id is assigned
// server-side on first donation and persisted in donor_voices. We keep this
// list intentionally identical in *order* to the server pool so the FNV-1a
// modulo lands on the same entry on both sides. Do NOT reorder or insert
// without changing the server file in lockstep.
//
// TODO(v2): when ElevenLabs preview clips are bundled, add a `previewUrl`
// here and wire a small play button on the donor flow + supporter page so
// donors can hear their voice before tipping.

export interface ClientVoice {
  name: string;
  vibe: string;
  gender: "m" | "f" | "n";
}

export const VOICE_LABELS: readonly ClientVoice[] = [
  { name: "Rachel",    vibe: "warm",         gender: "f" },
  { name: "Domi",      vibe: "spirited",     gender: "f" },
  { name: "Bella",     vibe: "soft",         gender: "f" },
  { name: "Antoni",    vibe: "calm",         gender: "m" },
  { name: "Elli",      vibe: "youthful",     gender: "f" },
  { name: "Josh",      vibe: "casual",       gender: "m" },
  { name: "Arnold",    vibe: "gruff",        gender: "m" },
  { name: "Adam",      vibe: "warm",         gender: "m" },
  { name: "Sam",       vibe: "neutral",      gender: "m" },
  { name: "Dorothy",   vibe: "kind",         gender: "f" },
  { name: "James",     vibe: "british",      gender: "m" },
  { name: "Thomas",    vibe: "thoughtful",   gender: "m" },
  { name: "Joseph",    vibe: "british",      gender: "m" },
  { name: "Ethan",     vibe: "young",        gender: "m" },
  { name: "Chris",     vibe: "friendly",     gender: "m" },
  { name: "Brian",     vibe: "deep",         gender: "m" },
  { name: "Daniel",    vibe: "british",      gender: "m" },
  { name: "Bill",      vibe: "older",        gender: "m" },
  { name: "Paul",      vibe: "documentary",  gender: "m" },
  { name: "Liam",      vibe: "articulate",   gender: "m" },
  { name: "Will",      vibe: "youthful",     gender: "m" },
  { name: "Jessica",   vibe: "expressive",   gender: "f" },
  { name: "Eric",      vibe: "neutral",      gender: "m" },
  { name: "Lily",      vibe: "british",      gender: "f" },
  { name: "Charlotte", vibe: "sultry",       gender: "f" },
  { name: "Alice",     vibe: "british",      gender: "f" },
  { name: "Matilda",   vibe: "warm",         gender: "f" },
  { name: "Aria",      vibe: "calm",         gender: "f" },
  { name: "Sarah",     vibe: "smooth",       gender: "f" },
  { name: "John",      vibe: "professional", gender: "m" },
  { name: "Laura",     vibe: "upbeat",       gender: "f" },
  { name: "Charlie",   vibe: "australian",   gender: "m" },
  { name: "George",    vibe: "british",      gender: "m" },
  { name: "Callum",    vibe: "scottish",     gender: "m" },
  { name: "River",     vibe: "androgynous",  gender: "n" },
  { name: "Will",      vibe: "modern",       gender: "m" },
  { name: "Lucy",      vibe: "energetic",    gender: "f" },
  { name: "Jacob",     vibe: "narration",    gender: "m" },
  { name: "Owen",      vibe: "young",        gender: "m" },
  { name: "Maya",      vibe: "calm",         gender: "f" },
  { name: "Diego",     vibe: "latino",       gender: "m" },
  { name: "Sofia",     vibe: "spanish",      gender: "f" },
  { name: "Hiro",      vibe: "japanese",     gender: "m" },
  { name: "Yuki",      vibe: "japanese",     gender: "f" },
  { name: "Mateus",    vibe: "brazilian",    gender: "m" },
  { name: "Anya",      vibe: "russian",      gender: "f" },
  { name: "Sven",      vibe: "nordic",       gender: "m" },
  { name: "Mei",       vibe: "chinese",      gender: "f" },
  { name: "Akira",     vibe: "calm",         gender: "n" },
  { name: "Zara",      vibe: "british",      gender: "f" },
];

// FNV-1a 32-bit — same algorithm as functions/lib/voices.ts and the wall
// renderer in LivingCast.tsx. Stable across browsers and Workers.
function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

export function voiceForHandle(handle: string): ClientVoice {
  const norm = (handle || "anon").trim().toLowerCase().replace(/^@/, "") || "anon";
  return VOICE_LABELS[fnv1a(norm) % VOICE_LABELS.length];
}
